import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to get current restaurant ID
const getRestaurantId = () => localStorage.getItem("selectedRestaurantId");

// Restaurants
export const getRestaurants = () => api.get("/restaurants");
export const createRestaurant = (data) => api.post("/restaurants", data);
export const updateRestaurant = (id, data) => api.put(`/restaurants/${id}`, data);
export const deleteRestaurant = (id) => api.delete(`/restaurants/${id}`);

// Vendors (with restaurant filter)
export const getVendors = () => api.get(`/vendors?restaurant_id=${getRestaurantId()}`);
export const createVendor = (data) => api.post(`/vendors?restaurant_id=${getRestaurantId()}`, data);
export const updateVendor = (id, data) => api.put(`/vendors/${id}`, data);
export const deleteVendor = (id) => api.delete(`/vendors/${id}`);
export const getUpcomingDeliveries = () => api.get(`/vendors/upcoming-deliveries?restaurant_id=${getRestaurantId()}`);

// Items (with restaurant filter)
export const getItems = (activeOnly = false) => api.get(`/items?restaurant_id=${getRestaurantId()}&active_only=${activeOnly}`);
export const createItem = (data) => api.post(`/items?restaurant_id=${getRestaurantId()}`, data);
export const quickAddItem = (data, overrideRestaurantId = null) => {
  const restaurantId = overrideRestaurantId || getRestaurantId();
  return api.post(`/items/quick-add?restaurant_id=${restaurantId}`, data);
};
export const updateItem = (id, data) => api.put(`/items/${id}`, data);
export const deleteItem = (id) => api.delete(`/items/${id}`);

// Categories (with restaurant filter)
export const getCategories = () => api.get(`/categories?restaurant_id=${getRestaurantId()}`);

// Bulk operations
export const downloadItemsTemplate = () => api.get("/items/template", { responseType: "blob" });
export const bulkImportItems = (formData) => api.post(`/items/bulk-import?restaurant_id=${getRestaurantId()}`, formData, {
  headers: { "Content-Type": "multipart/form-data" },
});
export const bulkDeleteItems = (itemIds) => api.post("/items/bulk-delete", { item_ids: itemIds });
export const bulkUpdateCategory = (itemIds, category) => api.post("/items/bulk-update-category", { item_ids: itemIds, category });

// Orders (with restaurant filter)
export const getOrders = (status = null) => {
  const params = new URLSearchParams({ restaurant_id: getRestaurantId() });
  if (status) params.append("status_filter", status);
  return api.get(`/orders?${params.toString()}`);
};
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post(`/orders?restaurant_id=${getRestaurantId()}`, data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const finalizeOrder = (id) => api.post(`/orders/${id}/finalize`);
export const matchVendor = (id, data) => api.post(`/orders/${id}/match-vendor`, data);
export const getOrderVendorsStatus = (id) => api.get(`/orders/${id}/vendors-status`);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const setPreferredRestaurant = (id) => api.put(`/restaurants/${id}/preferred`);
export const getDrafts = () => api.get(`/orders/drafts`);
export const saveDraft = (data) => api.post(`/orders/draft?restaurant_id=${getRestaurantId()}`, data);
export const deleteDraft = (id) => api.delete(`/orders/draft/${id}`);
export const resetAllOrders = () => api.post(`/orders/reset-all?restaurant_id=${getRestaurantId()}`);
export const downloadOrderPdf = async (id) => {
  const response = await api.get(`/orders/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `order_${id.substring(0, 8)}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Seed data
export const seedData = () => api.post("/seed");

// Receipts
export const uploadReceipt = (orderId, file) => {
  const formData = new FormData();
  formData.append("order_id", orderId);
  formData.append("file", file);
  return api.post("/receipts/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const getReceiptsForOrder = (orderId) => api.get(`/receipts/order/${orderId}`);
export const getReceipt = (receiptId) => api.get(`/receipts/${receiptId}`);
export const updateReceiptItems = (receiptId, items) => api.put(`/receipts/${receiptId}/items`, items);
export const deleteReceipt = (receiptId) => api.delete(`/receipts/${receiptId}`);
export const matchReceiptItem = (receiptId, receiptItemName, systemItemId, saveMapping = true) => 
  api.put(`/receipts/${receiptId}/match-item?receipt_item_name=${encodeURIComponent(receiptItemName)}&system_item_id=${systemItemId}&save_mapping=${saveMapping}`);

// Item Mappings
export const getItemMappings = () => api.get(`/item-mappings?restaurant_id=${getRestaurantId()}`);
export const createItemMapping = (data) => api.post(`/item-mappings?restaurant_id=${getRestaurantId()}`, data);
export const deleteItemMapping = (mappingId) => api.delete(`/item-mappings/${mappingId}`);

// Analytics
export const getAnalyticsSummary = (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.restaurantId) searchParams.append("restaurant_id", params.restaurantId);
  if (params.category) searchParams.append("category", params.category);
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  return api.get(`/analytics/summary?${searchParams.toString()}`);
};
export const getOrderAnalytics = (orderId) => api.get(`/analytics/order/${orderId}`);
export const getItemAnalytics = (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.restaurantId) searchParams.append("restaurant_id", params.restaurantId);
  if (params.category) searchParams.append("category", params.category);
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.sortBy) searchParams.append("sort_by", params.sortBy);
  return api.get(`/analytics/items?${searchParams.toString()}`);
};
export const getChartData = (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.restaurantId) searchParams.append("restaurant_id", params.restaurantId);
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  return api.get(`/analytics/chart-data?${searchParams.toString()}`);
};

// Get last ordered info for items (from confirmed receipts)
// combined=true will get data from ALL restaurants
export const getItemsLastOrdered = (combined = false) => 
  api.get(`/items/last-ordered?restaurant_id=${getRestaurantId()}&combined=${combined}`);

// Combined items - get items from ALL restaurants (for combined orders)
export const getCombinedItems = (activeOnly = false) => api.get(`/items/combined?active_only=${activeOnly}`);

// Combined vendors - get vendors from ALL restaurants
export const getCombinedVendors = () => api.get(`/vendors/combined`);

// Update item vendor (for vendor persistence during order creation)
export const updateItemVendor = (itemId, vendorId) => api.put(`/items/${itemId}/vendor`, { default_vendor_id: vendorId });

// Item Suggestions
export const getItemSuggestions = () => api.get(`/items/suggestions?restaurant_id=${getRestaurantId()}`);
export const getSuggestionCount = () => api.get(`/items/suggestions/count?restaurant_id=${getRestaurantId()}`);
export const createItemSuggestion = (data) => api.post(`/items/suggestions?restaurant_id=${getRestaurantId()}`, data);
export const reviewItemSuggestion = (suggestionId, data) => api.put(`/items/suggestions/${suggestionId}`, data);
export const getItemSuggestionsForItem = (itemId) => api.get(`/items/${itemId}/suggestions`);


// Customer Requests (public sign-ups from landing page)
// Public POST — send WITHOUT auth so unauthenticated visitors can submit.
export const createCustomerRequest = (data) =>
  axios.post(`${API_URL}/api/customer-requests`, data);
export const listCustomerRequests = () => api.get("/customer-requests");
export const customerRequestsCount = () => api.get("/customer-requests/count");
export const updateCustomerRequest = (id, data) => api.put(`/customer-requests/${id}`, data);
export const deleteCustomerRequest = (id) => api.delete(`/customer-requests/${id}`);
